import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  deleteBrand,
  getBrand,
  listBrands,
  uploadBrandLogo,
  upsertBrand,
} from "../services/brandService";
import { useBrandStore } from "../stores/brandStore";
import type {
  ReqDeleteBrand,
  ReqUploadBrandLogo,
  ReqUpsertBrand,
} from "../types/brand.types";

export const useBrandList = () =>
  useQuery({
    queryKey: ["brands"],
    queryFn: listBrands,
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useBrandDetail = (idx: number | null) =>
  useQuery({
    queryKey: ["brand", idx],
    queryFn: () => getBrand(idx!),
    enabled: idx != null,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });

export const useUpsertBrand = () => {
  const qc = useQueryClient();
  const closeDrawer = useBrandStore((s) => s.closeDrawer);

  return useMutation({
    mutationFn: (req: ReqUpsertBrand) => upsertBrand(req),
    onSuccess: () => {
      toast.success("브랜드 정보가 저장되었습니다.");
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["brand"] });
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

export const useDeleteBrand = () => {
  const qc = useQueryClient();
  const closeDrawer = useBrandStore((s) => s.closeDrawer);

  return useMutation({
    mutationFn: (req: ReqDeleteBrand) => deleteBrand(req),
    onSuccess: () => {
      toast.success("브랜드가 비활성화되었습니다.");
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["brand"] });
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`비활성화 실패: ${err.message}`);
    },
  });
};

export const useUploadBrandLogo = () =>
  useMutation({
    mutationFn: (req: ReqUploadBrandLogo) => uploadBrandLogo(req),
    onError: (err: Error) => {
      toast.error(`업로드 실패: ${err.message}`);
    },
  });
